import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import ActionPlanApi from "../../services/ActionPlanApi";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmModal } from "../../components/ConfirmModal/ConfirmModal";
import { useAuth } from "../../context/AuthContext";
import FileUploader from "../../components/FileUploader/FileUploader";
import CommentSection from "../../components/CommentSection/CommentSection";
import PageHeader from "../../components/PageHeader/PageHeader";
import FormCard from "../../components/FormCard/FormCard";
import Input from "../../components/Input/Input";

interface Option {
  label: string;
  value: string;
}

// Ajusta a data para fuso local removendo a hora (ignora UTC)
function parseDateOnly(dateString: string): Date {
  const [ymd] = dateString.split("T");
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(year, month - 1, day);
}

const ManageActionPlan: React.FC = () => {
  const { id, fmeaId, failureId } = useParams<{
    id: string;
    fmeaId: string;
    failureId: string;
  }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t } = useTranslation("form");
  const { userId, userName } = useAuth();

  const [plan, setPlan] = useState<any>({ cancelReason: "" });
  const [initialStatus, setInitialStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [newSeverity, setNewSeverity] = useState<number | null>(null);
  const [newOccurrence, setNewOccurrence] = useState<number | null>(null);
  const [newDetectability, setNewDetectability] = useState<number | null>(null);
  const [newRpn, setNewRpn] = useState<number>(0);
  const [newComment, setNewComment] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmBack, setShowConfirmBack] = useState(false);

  const isCompleted = initialStatus === "Completed";
  const isReadOnly = initialStatus === "Completed" || initialStatus === "Canceled";

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await ActionPlanApi.GetByIdAsync(id);
        const data = response.data;
        const cancelReason =
          data.cancelReason == null || data.cancelReason === "null"
            ? ""
            : data.cancelReason;
        const statusEnumMap = {
          0: "Planned",
          1: "Pending",
          2: "InProgress",
          3: "InTesting",
          4: "Completed",
          5: "Canceled",
        };
        const statusKey = response.data.status as keyof typeof statusEnumMap;
        const statusString = statusEnumMap[statusKey] ?? response.data.status;
        setPlan({
          ...response.data,
          cancelReason,
          status: statusString,
          forecastDate: parseDateOnly(response.data.forecastDate),
          completionDate: response.data.completionDate
            ? parseDateOnly(response.data.completionDate)
            : null,
        });

        if (response.data.implementationResult) {
          setNewSeverity(response.data.implementationResult.severity ?? null);
          setNewOccurrence(
            response.data.implementationResult.occurrence ?? null,
          );
          setNewDetectability(
            response.data.implementationResult.detectability ?? null,
          );
        }

        setInitialStatus(statusString);
        const commentsRes = await ActionPlanApi.GetCommentsAsync(id);
        setComments(commentsRes.data);
        setAttachments(response.data.files ?? []);
        
        const allStatus: Option[] = [
          { label: t("ActionPlanStatusPlanned"), value: "Planned" },
          { label: t("ActionPlanStatusPending"), value: "Pending" },
          { label: t("ActionPlanStatusInProgress"), value: "InProgress" },
          { label: t("ActionPlanStatusInTesting"), value: "InTesting" },
          { label: t("ActionPlanStatusCompleted"), value: "Completed" },
          { label: t("ActionPlanStatusCanceled"), value: "Canceled" },
        ];
        
        if (statusString === "Completed") {
          setStatusOptions([{ label: t("ActionPlanStatusCompleted"), value: "Completed" }]);
        } else if (statusString === "Pending") {
          setStatusOptions(allStatus);
        } else {
          setStatusOptions(allStatus.filter((s) => s.value !== "Pending"));
        }
      } catch (err) {
        displayToast("error", t("error"), t("errorloadMessage"));
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, t, displayToast, navigate]);

  useEffect(() => {
    if (plan.status === "Canceled" && plan.cancelReason == null) {
      setPlan((prev: any) => ({ ...prev, cancelReason: "" }));
    }
  }, [plan.status, plan.cancelReason]);

  useEffect(() => {
    const s = newSeverity || 0;
    const o = newOccurrence || 0;
    const d = newDetectability || 0;
    setNewRpn(s * o * d);
  }, [newSeverity, newOccurrence, newDetectability]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!plan?.name) newErrors.name = t("requiredField");
    if (!plan?.description) newErrors.description = t("requiredField");

    if (plan?.status === "Completed") {
      if (!plan.completionDate) {
        newErrors.completionDate = t("requiredField");
      } else if (new Date(plan.completionDate) > new Date()) {
        newErrors.completionDate = t("dueDateInvalid");
      }
      if (newSeverity === null) newErrors.newSeverity = t("requiredField");
      if (newOccurrence === null) newErrors.newOccurrence = t("requiredField");
      if (newDetectability === null) newErrors.newDetectability = t("requiredField");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (redirect = true): Promise<void> => {
    if (!validate()) {
      displayToast("warn", t("checkFieldsWarn"), "", false, 3000);
      return;
    }
    try {
      const formData = new FormData();
      Object.entries(plan).forEach(([key, value]) => {
        if (["files", "plantId", "PlantId"].includes(key)) return;
        if (key === "completionDate") {
          if (value instanceof Date) formData.append(key, value.toISOString());
          else if (value !== null && value !== undefined) formData.append(key, value as any);
          return;
        }
        if (key === "forecastDate" && value instanceof Date) formData.append(key, value.toISOString());
        else formData.append(key, value as any);
      });

      formData.append("plantId", plan.plantId);
      if (files.length > 0) files.forEach((file) => formData.append("Files", file));
      
      if (plan?.status === "Completed") {
        if (newSeverity !== null) formData.append("Severity", String(newSeverity));
        if (newOccurrence !== null) formData.append("Occurrence", String(newOccurrence));
        if (newDetectability !== null) formData.append("Detectability", String(newDetectability));
      }

      await ActionPlanApi.UpdateActionPlanAsync(formData, plan.id);
      displayToast("success", t("success"), t("successSaveMessage"));
      
      if (redirect) {
        navigate(`/fmeas/${fmeaId}/failures/${failureId}/action-plans`);
      }
    } catch (err: any) {
      displayToast("error", t("errorSaveMessage"), err?.data?.data?.message || t("errorSaveMessage"));
    }
  };

  const confirmBack = (): void => {
    setShowConfirmBack(true);
  };

  const handleAddComment = async (): Promise<void> => {
    if (!newComment.trim() || !id) return;
    try {
      const res = await ActionPlanApi.AddCommentAsync(id, newComment, userId);
      displayToast("success", t("success"), t("successCommentMessage"));
      setNewComment("");
      setComments((prev) => [{ ...res.data, userName: userName || res.data.userName }, ...prev]);
    } catch (err) {
      displayToast("error", t("error"), t("errorCommentMessage"));
    }
  };

  if (loading || !plan) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }} className="p-4">
      <ConfirmModal
        visible={showConfirmBack}
        onHide={() => setShowConfirmBack(false)}
        onConfirm={() => navigate(`/fmeas/${fmeaId}/failures/${failureId}/action-plans`)}
      />
      <PageHeader 
        title={t("manageActionPlan") || "Gerenciamento de Plano de Ação"}
        description={t("manageActionPlanDescription") || "Visualize e atualize os detalhes do plano de ação."}
      />

      <div className="grid mt-4" style={{ alignItems: "flex-start" }}>
        <div className="col-12 lg:col-8">
          <FormCard
            title={t("generalData") || "Dados Gerais"}
            icon="assignment"
            numColumns={2}
            onSubmit={() => handleSave()}
            submitButtonLabel={t("save") || "Salvar"}
            cancelButtonLabel={t("cancel") || "Cancelar"}
            onCancel={confirmBack}
            disabled={isReadOnly}
            className="mb-4"
          >
            <Input
              type="text"
              label={t("responsibleSector")}
              required
              value={plan.responsibleSector}
              onChange={(val) => setPlan({ ...plan, responsibleSector: val })}
              disabled={isReadOnly}
            />
            <Input
              type="text"
              label={t("responsiblePerson")}
              required
              value={plan.responsiblePerson}
              onChange={(val) => setPlan({ ...plan, responsiblePerson: val })}
              disabled={isReadOnly}
            />
            <div className="col-12">
              <Input
                type="text"
                label={t("operation")}
                value={plan.operationName}
                onChange={() => {}}
                disabled
              />
            </div>
            <div className="col-12">
              <Input
                type="text"
                label={t("title")}
                required
                value={plan.name}
                onChange={(val) => setPlan({ ...plan, name: val })}
                className={errors.name ? "p-invalid" : ""}
                disabled={isReadOnly}
              />
            </div>
            <div className="col-12">
              <Input
                type="textArea"
                label={t("description")}
                required
                value={plan.description}
                onChange={(val) => setPlan({ ...plan, description: val })}
                rows={4}
                className={errors.description ? "p-invalid" : ""}
                disabled={isReadOnly}
              />
            </div>
            <Input
              type="text"
              label={t("type")}
              required
              value={plan.type}
              onChange={(val) => setPlan({ ...plan, type: val })}
              disabled={isReadOnly}
            />
            <Input
              type="select"
              label={t("status")}
              required
              value={plan.status}
              options={statusOptions.map(opt => ({ id: opt.value, description: opt.label }))}
              onChange={(val) => setPlan({ ...plan, status: val })}
              disabled={isReadOnly}
            />
            <div className="col-12 md:col-6">
              <Input
                type="calendar"
                label={t("forecastDate")}
                required
                value={plan.forecastDate}
                onChange={(val) => setPlan({ ...plan, forecastDate: val })}
                disabled={isCompleted}
              />
            </div>

            {/* MOTIVO CANCELAMENTO (Dentro do Dados Gerais se for cancelado) */}
            {plan.status === "Canceled" && (
              <div className="col-12 mt-2">
                <Input
                  type="textArea"
                  label={t("cancelReason")}
                  required
                  value={plan.cancelReason}
                  onChange={(val) => setPlan({ ...plan, cancelReason: val })}
                  rows={3}
                  disabled={isReadOnly}
                />
              </div>
            )}

            {/* RESULTADO DA IMPLEMENTAÇÃO (Integrado no mesmo Card) */}
            {plan.status === "Completed" && (
              <div className="col-12 mt-4 pt-4 border-top-1 surface-border" style={{ gridColumn: "span 2" }}>
                <h4 className="mb-3">{t("implementationResult") || "Resultado da Implementação"}</h4>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <Input
                      type="calendar"
                      label={t("completionDate")}
                      required
                      value={plan.completionDate}
                      onChange={(val) => setPlan({ ...plan, completionDate: val })}
                      disabled={isCompleted}
                    />
                  </div>
                  <div className="grid col-12">
                    <div className="col-4">
                      <Input
                        type="text"
                        label={t("severity")}
                        required
                        value={newSeverity}
                        onChange={(val) => setNewSeverity(Number(val))}
                        disabled={isCompleted}
                      />
                    </div>
                    <div className="col-4">
                      <Input
                        type="text"
                        label={t("occurrence")}
                        required
                        value={newOccurrence}
                        onChange={(val) => setNewOccurrence(Number(val))}
                        disabled={isCompleted}
                      />
                    </div>
                    <div className="col-4">
                      <Input
                        type="text"
                        label={t("detectability")}
                        required
                        value={newDetectability}
                        onChange={(val) => setNewDetectability(Number(val))}
                        disabled={isCompleted}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <Input
                      type="text"
                      label="RPN"
                      value={String(newRpn)}
                      onChange={() => {}}
                      disabled
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ANEXOS (Integrado no mesmo Card) */}
            <div className="col-12 mt-4 pt-4 border-top-1 surface-border" style={{ gridColumn: "span 2" }}>
              <h4 className="mb-3">{t("attachments") || "Anexos"}</h4>
              <FileUploader
                files={files}
                onFilesChange={setFiles}
                existingFiles={attachments}
                disabled={isReadOnly}
                hideIcon
                hideChooseButton
                labels={{
                  title: "", // Vazio para não duplicar com o título acima
                  choose: t("attachFiles") || "Anexar Arquivos",
                  existingFilesSection: t("attachedFiles") || "Arquivos Anexados",
                }}
              />
            </div>
          </FormCard>
        </div>

        {/* COMENTÁRIOS */}
        <div className="col-12 lg:col-4">
          <FormCard
            title={t("comments") || "Comentários"}
            icon="chat"
            numColumns={1}
            onSubmit={(e) => e.preventDefault()}
            disabled={false}
          >
            <CommentSection
              comments={comments}
              value={newComment}
              onChange={setNewComment}
              onSubmit={handleAddComment}
              disabled={false}
              hideIcon
              className="p-0 border-none shadow-none" // Remove estilos internos que conflitam
              labels={{
                title: "", // Vazio para não duplicar com o FormCard
                placeholder: t("addNewComment") || "Adicionar novo comentário...",
                empty: t("noComments") || "Nenhum comentário.",
              }}
            />
          </FormCard>
        </div>
      </div>
    </div>
  );
};

export default ManageActionPlan;
